import { useState, useRef, useCallback, useEffect } from 'react';

const AUTOSAVE_KEY = 'wordriver_autosave';
const AUTOSAVE_TIMESTAMP_KEY = 'wordriver_autosave_time';

export const useSpeechRecognition = () => {
  // Load saved transcript from localStorage on init
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(AUTOSAVE_KEY) || '';
    }
    return '';
  });
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  const [lastSaved, setLastSaved] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(AUTOSAVE_TIMESTAMP_KEY);
      return saved ? new Date(saved) : null;
    }
    return null;
  });
  
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const isPausedRef = useRef(false);
  const languageRef = useRef('en-US');
  const restartTimeoutRef = useRef(null);

  // Auto-save transcript to localStorage whenever it changes
  useEffect(() => {
    if (transcript) {
      localStorage.setItem(AUTOSAVE_KEY, transcript);
      const now = new Date();
      localStorage.setItem(AUTOSAVE_TIMESTAMP_KEY, now.toISOString());
      setLastSaved(now);
    }
  }, [transcript]);

  // Keep refs in sync with state
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Initialize speech recognition once
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      setError('Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = languageRef.current;

    recognition.onstart = () => {
      console.log('Speech recognition started');
      setError(null);
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + ' ';
        } else {
          interim += result[0].transcript;
        }
      }

      if (finalTranscript) {
        setTranscript((prev) => prev + finalTranscript);
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      
      // These errors are recoverable - just restart
      if (event.error === 'no-speech' || event.error === 'aborted') {
        // Will auto-restart via onend
        return;
      }
      
      if (event.error === 'not-allowed') {
        setError('Microphone permission denied. Please allow microphone access and refresh the page.');
        setIsListening(false);
        isListeningRef.current = false;
      } else if (event.error === 'network') {
        setError('Network error. Please check your internet connection.');
      } else if (event.error === 'audio-capture') {
        setError('No microphone found. Please connect a microphone.');
        setIsListening(false);
        isListeningRef.current = false;
      } else {
        // For other errors, show but don't stop
        console.warn('Speech recognition error:', event.error);
      }
    };

    recognition.onend = () => {
      console.log('Speech recognition ended. isListening:', isListeningRef.current, 'isPaused:', isPausedRef.current);
      
      // Clear any pending restart
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      
      // Auto-restart if still supposed to be listening and not paused
      if (isListeningRef.current && !isPausedRef.current) {
        restartTimeoutRef.current = setTimeout(() => {
          if (isListeningRef.current && !isPausedRef.current && recognitionRef.current) {
            try {
              console.log('Auto-restarting speech recognition...');
              recognitionRef.current.start();
            } catch (e) {
              console.log('Restart error:', e.message);
              // If already started, that's fine
              if (!e.message.includes('already started')) {
                setError('Could not restart recognition. Please click Start again.');
                setIsListening(false);
                isListeningRef.current = false;
              }
            }
          }
        }, 100);
      }
    };

    recognition.onspeechstart = () => {
      console.log('Speech detected');
    };

    recognition.onspeechend = () => {
      console.log('Speech ended');
    };

    recognitionRef.current = recognition;

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []); // Only run once on mount

  const startListening = useCallback((lang = 'en-US') => {
    if (!recognitionRef.current) {
      setError('Speech recognition not initialized. Please refresh the page.');
      return;
    }
    
    // Stop any existing session first
    try {
      recognitionRef.current.stop();
    } catch (e) {}
    
    // Small delay before starting fresh
    setTimeout(() => {
      languageRef.current = lang;
      recognitionRef.current.lang = lang;
      setError(null);
      setIsPaused(false);
      isPausedRef.current = false;
      setIsListening(true);
      isListeningRef.current = true;
      
      try {
        console.log('Starting speech recognition with language:', lang);
        recognitionRef.current.start();
      } catch (e) {
        console.error('Start error:', e);
        if (e.message.includes('already started')) {
          // Already running, that's fine
        } else {
          setError('Could not start recognition. Please try again.');
          setIsListening(false);
          isListeningRef.current = false;
        }
      }
    }, 150);
  }, []);

  const stopListening = useCallback(() => {
    console.log('Stopping speech recognition');
    
    // Clear any pending restart
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }
    
    setIsListening(false);
    isListeningRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    setInterimTranscript('');
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
  }, []);

  const pauseListening = useCallback(() => {
    console.log('Pausing speech recognition');
    
    // Clear any pending restart
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }
    
    setIsPaused(true);
    isPausedRef.current = true;
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
  }, []);

  const resumeListening = useCallback(() => {
    if (!recognitionRef.current) return;
    
    console.log('Resuming speech recognition');
    setIsPaused(false);
    isPausedRef.current = false;
    setError(null);
    
    try {
      recognitionRef.current.start();
    } catch (e) {
      console.error('Resume error:', e);
      if (!e.message.includes('already started')) {
        setError('Could not resume. Please try again.');
      }
    }
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    // Also clear from localStorage
    localStorage.removeItem(AUTOSAVE_KEY);
    localStorage.removeItem(AUTOSAVE_TIMESTAMP_KEY);
    setLastSaved(null);
  }, []);

  const changeLanguage = useCallback((lang) => {
    languageRef.current = lang;
    if (recognitionRef.current) {
      recognitionRef.current.lang = lang;
      
      // Restart if currently listening
      if (isListeningRef.current && !isPausedRef.current) {
        try {
          recognitionRef.current.stop();
          setTimeout(() => {
            if (isListeningRef.current && !isPausedRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {}
            }
          }, 150);
        } catch (e) {}
      }
    }
  }, []);

  return {
    isListening,
    isPaused,
    transcript,
    interimTranscript,
    error,
    isSupported,
    lastSaved,
    startListening,
    stopListening,
    pauseListening,
    resumeListening,
    clearTranscript,
    changeLanguage,
  };
};
